import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core'
import { CommonModule, NgIf, AsyncPipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { IonicModule } from '@ionic/angular'
import { AlertController, LoadingController, ModalController } from '@ionic/angular'
import { firstValueFrom } from 'rxjs'
import { Assistance } from '../../../models/assistance'
import { AssistanceJournalEntry } from '../../../models/EditorAssistance'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { AssistanceService } from '../../../services/domain/assistance/assistance.service'
import { AssistantService } from '../../../services/domain/assistant/assistant.service'
import { AssistanceStatusManagerService } from '../../../services/assistance-status-manager.service'
import { ToasterService } from '../../notification/toaster.service'
import { RoleAssistantFab, RoleAssistantFabState } from './roleAssistantFab'
import { AssistantAssignment } from '../../../models/assistantAssignment'
import { UsersService } from '../../../services/domain/users.service'
import { User } from '../../../models/user'
import { DateRangeViewComponent } from '../../../components/date-range-view/date-range-view.component'
import { Observable } from 'rxjs'
import { DateUtils } from '../../../services/date-utils.service'
import { BypassHtmlSanitizerPipe } from '../../../shared/pipes/bypass-html-sanitizer.pipe'
import { GetPrimaryContactPipe } from '../../../shared/pipes/get-primary-contact.pipe'
import { TagEditorComponent } from '../../event/components/tag-editor/tag-editor.component'
import { AssistanceCareLogEditorComponent } from '../../assistance/assistance-care-log-editor/assistance-care-log-editor.component'
import { CareLogEditorAssistant } from '../../../models/assistance-care-log.model'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'

@Component({
  selector: 'app-role-assistant-assistance-editor',
  templateUrl: './role-assistant-assistance-editor.component.html',
  styleUrls: ['./role-assistant-assistance-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    DateRangeViewComponent,
    BypassHtmlSanitizerPipe,
    GetPrimaryContactPipe,
    TagEditorComponent,
    AssistanceCareLogEditorComponent,
    TranslocoModule,
    NgIf,
    AsyncPipe,
  ],
})
export class RoleAssistantAssistanceEditorComponent implements OnInit, OnChanges {
  private modalCtrl = inject(ModalController)
  private alertCtrl = inject(AlertController)
  private assistanceService = inject(AssistanceService)
  private statusManagerService = inject(AssistanceStatusManagerService)
  private toaster = inject(ToasterService)
  private usersService = inject(UsersService)
  private loading = inject(LoadingController)
  private assistantService = inject(AssistantService)
  private transloco = inject(TranslocoService)

  @Input() assistantId: number
  @Input() assistance: Assistance

  public assignment: AssistantAssignment
  private _mappedAssistantAssignment: CareLogEditorAssistant[] = []
  myJournalEntry = ''

  totalCareLogMinutes = 0
  savingJournalEntry = false

  private assistanceCurrentlyRunning = false
  fab = new RoleAssistantFab()
  RoleAssistantFabState = RoleAssistantFabState
  CONFIRMED: AssistanceStatusName = AssistanceStatusName.CONFIRMED
  keyCoordinator$: Observable<User>

  async ngOnInit() {
    console.log('modal onInit', this.assistantId, this.assistance)
    await this.initAssistance(this.assistance)
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        if (propName === 'assistance') {
          void this.initAssistance(this.assistance)
        }
      }
    }
  }

  dismissModal() {
    this.modalCtrl.dismiss()
  }

  async confirmAssistance() {
    console.log(this.assignment)

    // warning - this mutates this.assignment
    if (
      await this.statusManagerService.advanceAssignmentStatusWithGlobalValidationMutating(
        this.assistance,
        this.assignment,
        true,
      )
    ) {
      this.fab.set(RoleAssistantFabState.working)

      await this.assistanceService.patchAssignment(this.assignment)

      this.updateFab(this.assistance)
      await this.toaster.simpleToast('Asistence byla úspěšně potvrzena.')
    }
  }

  async startAssistance() {
    this.fab.set(RoleAssistantFabState.working)

    if (!this.assistance.realizedFrom) {
      this.assistance = await this.assistanceService.patchNonRecurrently({
        id: this.assistance.id,
        realizedFrom: new Date().getTime(),
      })
      await this.toaster.simpleToast('Asistence byla úspěšně započata.')
    }

    this.assistanceCurrentlyRunning = true
    this.fab.set(RoleAssistantFabState.assistanceReadyToEnd)
  }

  private updateFab(assistance: Assistance) {
    if (assistance.realizedFrom && assistance.realizedTo) {
      this.fab.set(RoleAssistantFabState.assistanceFinished)
      return
    }

    if (assistance.realizedFrom && !assistance.realizedTo) {
      this.assistanceCurrentlyRunning = true
      this.fab.set(RoleAssistantFabState.assistanceReadyToEnd)
      return
    }

    if (this.assignment.status.name === AssistanceStatusName.SENT_TO_ASSISTANT) {
      this.fab.set(RoleAssistantFabState.assistanceReadyToBeConfirmed)
      return
    }

    if (this.assistanceService.assistanceCanBeStartedByAssistant(assistance, this.assistantId)) {
      this.fab.set(RoleAssistantFabState.assistanceReadyToStart)
      return
    }

    this.fab.set(RoleAssistantFabState.none)
  }

  get canWriteJournalEntry(): boolean {
    if (!this.assistance || !this.assignment) return false
    return (
      !!this.assistance.realizedFrom ||
      !!this.assistance.realizedTo ||
      (this.assistance.status?.name === AssistanceStatusName.CONFIRMED &&
        this.assignment.status?.name === AssistanceStatusName.CONFIRMED)
    )
  }

  get assistanceElapsedMinutes(): number | null {
    if (!this.assistance?.realizedFrom) return null
    const endMs = this.assistance.realizedTo
      ? new Date(this.assistance.realizedTo).getTime()
      : Date.now()
    return Math.floor(
      (endMs - new Date(this.assistance.realizedFrom).getTime()) / 60_000,
    )
  }

  get mappedAssistantAssignment(): CareLogEditorAssistant[] {
    return this._mappedAssistantAssignment
  }

  private async enrichJournalEntriesForDisplay(assistance: Assistance): Promise<void> {
    const entries = assistance.journalEntries
    if (!entries?.length) return

    const enriched = await Promise.all(
      entries.map(async (entry: AssistanceJournalEntry): Promise<{ assistantId: number; content: string } | null> => {
        if (entry.assistantId) {
          return { assistantId: entry.assistantId, content: entry.content ?? '' }
        }
        if (entry.authorUserId) {
          const assistantId = await firstValueFrom(
            this.assistantService.getAssistantIdByUserId(entry.authorUserId),
          )
          return { assistantId, content: entry.content ?? '' }
        }
        return null
      }),
    )
    assistance.journalEntries = enriched.filter(Boolean) as { assistantId: number; content: string }[]
  }

  private async initAssistance(assistance: Assistance) {
    await this.enrichJournalEntriesForDisplay(assistance)
    this.assignment = this.assistance.assistantAssignments.getAssignmentByAssistantId(this.assistantId)
    this._mappedAssistantAssignment = this.assignment
      ? [
          {
            assignmentId: this.assignment.id,
            assistantId: this.assistantId,
            fullname: this.assignment.assistant?.fullname ?? '',
          },
        ]
      : []
    this.keyCoordinator$ = this.usersService.getUserById(this.assistance.client.keyCoordinatorId)
    this.myJournalEntry =
      assistance.journalEntries?.find((e) => e.assistantId === this.assistantId)?.content ?? ''

    console.log('initAssistance', this.assignment)

    this.updateFab(assistance)
  }

  onMyJournalEntryChange(content: string): void {
    this.myJournalEntry = content
  }

  async saveJournalEntry(): Promise<void> {
    this.savingJournalEntry = true
    try {
      const entries: AssistanceJournalEntry[] = [...(this.assistance.journalEntries ?? [])]
      const idx = entries.findIndex((e) => e.assistantId === this.assistantId)
      const entry: AssistanceJournalEntry = { assistantId: this.assistantId, content: this.myJournalEntry }
      if (idx >= 0) entries[idx] = entry
      else entries.push(entry)

      const result = await this.assistanceService.patchNonRecurrently({
        id: this.assistance.id,
        journalEntries: entries,
      })
      await this.enrichJournalEntriesForDisplay(result)
      this.assistance = result
      this.assistance.journalEntries = this.assistance.journalEntries ?? entries
      await this.toaster.simpleToast(this.transloco.translate('ASSISTANCE_CARE_LOG_EDITOR.TOAST_ENTRY_SAVED'))
    } catch {
      await this.toaster.simpleToast(this.transloco.translate('ASSISTANCE_CARE_LOG_EDITOR.TOAST_ENTRY_SAVE_FAILED'))
    } finally {
      this.savingJournalEntry = false
    }
  }

  async askEndAssistance() {
    const alert = await this.alertCtrl.create({
      header: 'Ukončit asistenci?',
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: 'Ano',
          cssClass: 'primary',
          role: 'confirm',
          handler: async () => {
            await this.endAssistanceWithSavingPopup()
            await alert.dismiss()
          },
        },
      ],
    })

    await alert.present()
  }

  private async endAssistanceWithSavingPopup() {
    const endTime = new Date()

    const loading = await this.loading.create({
      cssClass: 'loadingBanner',
      message: `Ukončuji asistenci s časem ${DateUtils.dateToHhColonMm(endTime)}`,
      backdropDismiss: false,
      spinner: 'dots',
    })
    await loading.present()

    await this.endAssistance(endTime)
    await loading.dismiss()
  }

  private async endAssistance(endTime: Date) {
    this.fab.set(RoleAssistantFabState.working)
    this.assistance = await this.assistanceService.patchNonRecurrently({
      id: this.assistance.id,
      realizedTo: endTime.getTime(),
    })
    this.assistanceCurrentlyRunning = false
    this.fab.set(RoleAssistantFabState.assistanceFinished)

    await this.toaster.simpleToast('Asistence byla úspěšně ukončena.')
  }
}
